import { Button, Flex, Form, Input, Switch } from "antd";
import { useEffect } from "react";
import {
  useCreateTagMutation,
  useUpdateTagMutation,
} from "../../../../../app/features/admin/admin.api";
import type { Tag } from "../../../../../app/features/admin/admin.types";
import CustomModal from "../../../../../components/CustomModal";
import type { ModalType } from "../../../../../hooks/useModal";
import { toast } from "../../../../../lib/toast";
import { slug } from "../../../../../utils/string.types";

interface TagModalProps {
  open: boolean;
  onCancel: () => void;
  initialValues?: Tag | null;
  type?: ModalType["type"];
}

const TagModal = ({ open, onCancel, initialValues, type }: TagModalProps) => {
  const [form] = Form.useForm();
  const [createTag, { isLoading: isCreating }] = useCreateTagMutation();
  const [updateTag, { isLoading: isUpdating }] = useUpdateTagMutation();

  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (initialValues && type === "UPDATE") {
      form.setFieldsValue({
        name: initialValues.name,
        slug: initialValues.slug,
        isActive: initialValues.isActive,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        isActive: true,
      });
    }
  }, [initialValues, form, open, type]);

  const handleSubmit = async (values: any) => {
    try {
      let res;
      if (initialValues?.id && type === "UPDATE") {
        res = await updateTag({
          id: initialValues.id,
          ...values,
          slug: initialValues?.slug || slug(values.name),
        }).unwrap();
      } else {
        res = await createTag({ ...values, slug: slug(values.name) }).unwrap();
      }

      if (res) {
        toast(
          "success",
          type === "UPDATE"
            ? "Tag updated successfully"
            : "Tag created successfully",
        );
        onCancel();
      }
    } catch (error: any) {
      toast("error", error?.data?.message || "Something went wrong");
    }
  };

  return (
    <CustomModal
      handleClose={onCancel}
      title={type === "UPDATE" ? "Update Tag" : "Create Tag"}
      open={open}
      width={500}
      footer
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ isActive: true }}
        className="custom-form"
        size="large"
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Please enter tag name" }]}
        >
          <Input placeholder="Enter tag name" />
        </Form.Item>

        {/* <Form.Item
          label="Slug"
          name="slug"
          rules={[{ required: true, message: "Please enter tag slug" }]}
        >
          <Input placeholder="Enter tag slug" />
        </Form.Item> */}

        <Form.Item label="Status" name="isActive" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>

        <Flex justify="end" gap={8}>
          <Button
            className="min-w-[120px]"
            disabled={isLoading}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="min-w-[120px]"
            htmlType="submit"
            loading={isLoading}
          >
            {type === "UPDATE" ? "Update" : "Create"}
          </Button>
        </Flex>
      </Form>
    </CustomModal>
  );
};

export default TagModal;
